import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart, faRedo } from '@fortawesome/free-solid-svg-icons'

import { getChannelClient } from 'src/utils/messages/singleton'

import Comment from '../Comment'
import Share from '../Share'

// Confirmation page shown after a successful donation. Uses the v7-only
// `confirmation` locale block so the v6 `thankyou` copy can keep its
// nonprofit-profile-link wording without having to drag v7 along. The
// message uses the donation amount (not total) per design — the receipt
// line is fine print below.
//
// Stock (and future DAF) hand us a `pending: true` donation: the actual
// amount + donor email come from the third-party SDK via webhook later,
// not at the moment the donor finishes inside the SDK. Render a
// generic "your donation is on its way" message in that case rather
// than showing "$ is confirmed" with a missing amount.
const ThankYou = ({
  i18n, donation, plugins, comment, donationsPath,
  showShare, shareModal, donateAgain,
  blank_modal_src, emoji_picker_loader_src, widget_instance_token,
}) => {
  if (!donation) return null

  const pending = !!donation.pending
  const amount = pending ? null : i18n.formatCurrency(donation.amount, donation.currency)
  const email = donation.email

  // Post-donation comment fallback: only when the partner enabled
  // comments AND the donor didn't already commit one during the
  // pre-donation flow (extras.comment.submitted=true). Saves through
  // the existing widgets/donations#comment endpoint, then reuses the
  // same extras.comment.save to flip submitted → true so the link
  // collapses into the green confirmed state. editable=false because
  // the email notification has gone out by this point — donor can't
  // amend after publication.
  const showCommentFallback = comment?.enabled && !comment.submitted

  const submitPostDonationComment = async text => {
    const response = await fetch(`${donationsPath}/${donation.token}/comment`, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ comment: text }),
    })
    if (!response.ok) throw new Error(i18n.t('ajax_error'))
    const data = await response.json()
    // Forward the new Comment row to the parent FLP so its donor
    // feed renders it immediately. Without this, the donor sees
    // their comment confirmed in the widget but it doesn't appear
    // in the FLP feed until the next polling cycle / page reload.
    // Same channel message v6/Comment.jsx sends.
    getChannelClient().send('commentAdded', data)
    comment.save(text)
  }

  // aria-live="assertive" announces the success state immediately on
  // mount — donation completion is the most important state change in
  // the flow, and we want a screen reader to surface it without
  // queueing behind anything else. The panel's region+label
  // announcement on focus shift covers the same ground politely; the
  // live region is the assertive belt-and-braces.
  return (
    <div className="text-center py-4" aria-live="assertive">
      {plugins.renderSlot('beforeThankYou', { donation })}
      <div className="text-brand mb-4">
        <FontAwesomeIcon icon={faHeart} size="4x" />
      </div>
      <h2 className="text-brand text-2xl font-semibold mb-4">
        {i18n.t('confirmation.heading')}
      </h2>
      <p>{pending
        ? i18n.t('confirmation.message_pending')
        : i18n.t('confirmation.message', { amount })
      }</p>
      {pending ? (
        <p className="text-sm text-gray-500">
          {i18n.t('confirmation.receipt_pending')}
        </p>
      ) : email && (
        <p className="text-sm text-gray-500">
          {i18n.t('confirmation.receipt', { email })}
        </p>
      )}

      {showCommentFallback && (
        <div className="mt-6">
          <Comment
            i18n={i18n}
            id="postDonationCommentModal"
            value={comment.value}
            submitted={comment.submitted}
            onSave={submitPostDonationComment}
            editable={false}
            blank_modal_src={blank_modal_src}
            emoji_picker_loader_src={emoji_picker_loader_src}
            widget_instance_token={widget_instance_token}
          />
        </div>
      )}

      {/* Post-donation actions. Mirrors v6/Finished.jsx: render only when
          either is enabled by partner config. Same visual hierarchy as
          v6: Share is btn-primary, Donate Again wears the rainbow
          gradient (.btn-donate-again) so it outshines Share as the
          louder call to action. flex-wrap lets the row stack on
          narrow widths. */}
      {(showShare || donateAgain) && (
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {showShare && <Share i18n={i18n} shareModal={shareModal} />}
          {donateAgain && (
            <button
              type="button"
              onClick={donateAgain}
              className="btn btn-donate-again inline-flex items-center justify-center gap-2"
            >
              <FontAwesomeIcon icon={faRedo} />
              {i18n.t('donate_again_button')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}

export default ThankYou
